import { ref, computed } from 'vue';
import axios from 'axios';
import { defineStore } from 'pinia';

import { useAccessStore } from '@/stores/access';
import { useLicenseStore } from '@/stores/licenseStore';

import Consts from '@/api/Consts';

import type {
	BaseStationsTableColumnKey,
	MenuItemKey,
	TokenLoginRaw,
	UserProfileConf,
	UserRole,
	VisibleRoleOption,
} from '@/stores/types/userTypes';
import { isBoolean, isNumber, isOneOf, isRecord, isString } from '@/utils/typeGuards';
import { readVersionedLocalStorage } from '@/utils/versionedLocalStorage';


const PROFILE = 'profile';
const VERSION = 2;


type UserState = {
	enabled: boolean;
	role: UserRole;
};


const USER_ROLES = ['admin', 'demo_admin', 'rover', 'unauthorized', ''] as const satisfies readonly UserRole[];


const isUserRole = (value: unknown): value is UserRole => isOneOf(value, USER_ROLES);


const isUserProfileConf = (value: unknown): value is UserProfileConf => {
	if (!isRecord(value)) {
		return false;
	}
	return (
		isNumber(value.version) &&
		isString(value.token) &&
		isString(value.username) &&
		isUserRole(value.role) &&
		isBoolean(value.enabled)
	);
};


const updateHeaders = (isAuthenticated: boolean, token: string): void => {
	axios.defaults.headers.common['Authorization'] = isAuthenticated ? ('Token ' + token) : '';
};


const isEmpty = (value: string | null | undefined): boolean => value == null || value.length === 0;


const defaultProfile = (): UserProfileConf => ({
	token: '',
	username: '',
	role: '',
	enabled: false,
	version: VERSION,
});


const readProfile = (): UserProfileConf => (
	readVersionedLocalStorage(PROFILE, VERSION, isUserProfileConf, defaultProfile())
);


const persistProfile = (
	token: string,
	username: string,
	role: UserRole,
	enabled: boolean,
): void => {
	const conf = {
		token,
		username,
		role,
		enabled,
		version: VERSION,
	} satisfies UserProfileConf;
	localStorage.setItem(PROFILE, JSON.stringify(conf));
};


const checkIsAuthenticated = (token: string, username: string, role: UserRole): boolean => (
	!(isEmpty(token) || isEmpty(username) || isEmpty(role) || role === 'unauthorized')
);


const isAdmin = (state: UserState): boolean => state.enabled && state.role === 'admin';
const isDemoAdmin = (state: UserState): boolean => state.enabled && state.role === 'demo_admin';
const isAdminOrDemoAdmin = (state: UserState): boolean => (
	state.enabled && (state.role === 'admin' || state.role === 'demo_admin')
);
const isRover = (state: UserState): boolean => state.enabled && state.role === 'rover';
const isAdminOrRover = (state: UserState): boolean => (
	state.enabled && (state.role === 'admin' || state.role === 'rover')
);
const isAdminOrRoverOrDemoAdmin = (state: UserState): boolean => (
	state.enabled && (state.role === 'admin' || state.role === 'rover' || state.role === 'demo_admin')
);


export const useUserStore = defineStore('user', () => {
	const profile = readProfile();
	const { token, username, role, enabled } = profile;
	updateHeaders(checkIsAuthenticated(token, username, role), token);

	const tokenRef = ref(token);
	const usernameRef = ref(username);
	const roleRef = ref<UserRole>(role);
	const enabledRef = ref(enabled);
	const settingsSpecialModeActiveRef = ref(false);
	const userState = computed((): UserState => ({ enabled: enabledRef.value, role: roleRef.value }));

	const accessStore = useAccessStore();

	const isAuthenticated = computed((): boolean => (
		checkIsAuthenticated(tokenRef.value, usernameRef.value, roleRef.value)
	));

	const menuItems = computed((): { key: MenuItemKey }[] => {
		const license = useLicenseStore();
		const state = userState.value;
		const authed = isAuthenticated.value;
		const admin = isAdminOrDemoAdmin(state);
		const adminOrRover = isAdminOrRover(state);
		const rover = isRover(state);
		const showHomeMap = enabledRef.value || accessStore.allowReadOnlyUnauthorizedAccess;

		const entries: { key: MenuItemKey; when: boolean }[] = [
			{ key: 'home-map', when: showHomeMap },
			{ key: 'home-public', when: !showHomeMap },
			{ key: 'login', when: !authed },
			{ key: 'base-stations', when: adminOrRover },
			{ key: 'baselines', when: admin && license.allowBaselines },
			{ key: 'rovers', when: admin },
			{ key: 'stats', when: admin },
			{ key: 'events', when: admin },
			{ key: 'settings', when: admin },
			{ key: 'dashboard', when: admin && license.featureFlagDashboard },
			{ key: 'cnet-constructor', when: admin && license.featureFlagCnetConstructor && license.allowPppGp },
			{ key: 'insights', when: admin && license.featureFlagInsights },
			{ key: 'rovers', when: rover },
			{ key: 'info', when: rover },
			{ key: 'account', when: authed },
			{ key: 'logout', when: authed },
		];

		return entries.filter(entry => entry.when).map(({ key }) => ({ key }));
	});

	const isBaseStationChangeAllowed = computed((): boolean => isAdmin(userState.value));
	const allowToSeeModuleStatuses = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	const allowToSeeMountpointNames = computed((): boolean => (
		isAdminOrRoverOrDemoAdmin(userState.value) || accessStore.showMountpointsForUnauthorizedUsers
	));
	const allowToSeeLiveStatus = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	const allowToChangeMapMode = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	const allowToSeePaas = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	const allowToUpdateLicense = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	const allowExternalApiKeyManagement = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	// allowBaselines only for external use. For internal use, use isAdminOrDemoAdmin(state). Avoid circular dependency.
	const allowBaselines = computed((): boolean => (
		isAdminOrDemoAdmin(userState.value) && useLicenseStore().allowBaselines
	));
	const showSubNetworkFilters = computed((): boolean => isAdminOrDemoAdmin(userState.value));
	const maxZoom = computed((): number => (isAdminOrRoverOrDemoAdmin(userState.value) ? 30 : 10));
	const allowBaseStationsPageFull = computed((): boolean => isAdmin(userState.value));
	const baseStationsPageColumns = computed(() => {
		let columns: readonly BaseStationsTableColumnKey[] = ['status', 'statusOnline', 'mountpoints'];
		if (allowBaseStationsPageFull.value) {
			columns = ['statusMerged', 'mountpoints', 'label', 'antDetails', 'actions', 'displayAddress'];
		}
		const columnKeys = new Set<string>(columns);
		return Consts.BASE_STATIONS_TABLE_COLUMNS.filter(item => item.key != null && columnKeys.has(item.key));
	});
	const visibleRoles = computed((): VisibleRoleOption[] => {
		let roles: VisibleRoleOption[] = [];
		if (isAdmin(userState.value)) {
			roles = [{ value: 'admin' }, { value: 'rover' }, { value: 'demo_admin' }];
		}
		if (isDemoAdmin(userState.value)) {
			roles = [{ value: 'rover' }];
		}
		return roles;
	});

	const login = (data: TokenLoginRaw): void => {
		tokenRef.value = data.auth_token;
		usernameRef.value = data.username;
		update(data.role, data.enabled);
	};

	const update = (
		newRole: UserRole,
		newEnabled: boolean,
		settingsSpecialModeActive?: boolean,
	): void => {
		if (
			newRole === 'unauthorized' &&
			checkIsAuthenticated(tokenRef.value, usernameRef.value, roleRef.value)
		) {
			return;
		}
		roleRef.value = newRole;
		enabledRef.value = newEnabled;
		if (settingsSpecialModeActive !== undefined) {
			settingsSpecialModeActiveRef.value = settingsSpecialModeActive;
		}
		persistProfile(tokenRef.value, usernameRef.value, roleRef.value, enabledRef.value);
		updateHeaders(isAuthenticated.value, tokenRef.value);
	};

	const logout = (): void => {
		tokenRef.value = '';
		usernameRef.value = '';
		roleRef.value = '';
		enabledRef.value = false;
		settingsSpecialModeActiveRef.value = false;
		localStorage.removeItem(PROFILE);
		updateHeaders(isAuthenticated.value, tokenRef.value);
	};

	return {
		token: tokenRef,
		username: usernameRef,
		role: roleRef,
		enabled: enabledRef,
		settingsSpecialModeActive: settingsSpecialModeActiveRef,
		menuItems,
		isAuthenticated,
		isBaseStationChangeAllowed,
		allowToSeeModuleStatuses,
		allowToSeeMountpointNames,
		allowToSeeLiveStatus,
		allowToChangeMapMode,
		allowToSeePaas,
		allowToUpdateLicense,
		allowExternalApiKeyManagement,
		allowBaselines,
		showSubNetworkFilters,
		maxZoom,
		allowBaseStationsPageFull,
		baseStationsPageColumns,
		visibleRoles,
		login,
		update,
		logout,
	};
});
